import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { formatMD, useAuthStore } from '@indipia/shared'
import {
  orderApi, OrderApiError, ORDER_ERROR_NETWORK, toOrderApiError,
  type OrderBatchItem, type OrderScheduleResult, type OrderSendResult, type OrderSettingItem, type OrderVendorRow,
  type ScheduleTaskOrderItem, type VendorCandidatesResult, type VendorInfoLite,
} from '../api/orderApi'
import {
  draftFromRow, emptyDraftItem, hasDraftErrors, mapServerField, ORDER_MESSAGE, orderErrorMessage, toOrderPatch,
  validateDraft, type DraftErrors, type OrderDraft,
} from '../constants/materialOrder.logic'
import { useInvalidateSignals } from './useTaskSignals'

// C-3 팝업·상세창 카드·H-5 공용 조회·행동·자동저장 훅 (WP5 5단계 U0-3).
// 쓰기는 자동 재시도하지 않는다. 오류는 OrderApiError 그대로 던져 호출 화면이 orderErrorMessage 로 표시한다.

/** 발주 데이터 쿼리 키(Q1·A1·Q3 묶음) */
export const MATERIAL_ORDERS_KEY = 'material-orders'
export const VENDOR_DIRECTORY_KEY = 'vendor-directory'
export const VENDOR_CANDIDATES_KEY = 'vendor-candidates'

/** 입력 초안 자동저장 대기(ms) */
export const DRAFT_SAVE_DEBOUNCE_MS = 600
/** '저장됐어요' 표시 시간(ms) */
export const DRAFT_SAVED_VISIBLE_MS = 2000

// ── 조회 ──────────────────────────────────────────────────────────────

interface MaterialOrderBundle {
  tasks: ScheduleTaskOrderItem[]
  rows: OrderVendorRow[]
  settings: OrderSettingItem[]
}

type QueryStatus = 'loading' | 'ready' | 'error'

const EMPTY_TASKS: ScheduleTaskOrderItem[] = []
const EMPTY_ROWS: OrderVendorRow[] = []
const EMPTY_SETTINGS: OrderSettingItem[] = []

/**
 * Q1(공종)·A1(거래처 행)·Q3(발주 설정)를 한 번에 조회 — 하나라도 실패하면 error(일부만 그려 폴백하지 않음).
 * 팝업·카드가 같은 키를 공유한다. 언마운트 시 요청 취소.
 */
export function useMaterialOrderData(scheduleId: number | null | undefined): {
  status: QueryStatus
  tasks: ScheduleTaskOrderItem[]
  rows: OrderVendorRow[]
  settings: OrderSettingItem[]
  reload(): void
} {
  const q = useQuery<MaterialOrderBundle>({
    queryKey: [MATERIAL_ORDERS_KEY, scheduleId],
    queryFn: async ({ signal }) => {
      const [tasks, rows, settings] = await Promise.all([
        orderApi.getScheduleOrders(scheduleId as number, signal),
        orderApi.getOrderVendors(scheduleId as number, signal),
        orderApi.getOrderSettings(signal),
      ])
      return { tasks, rows, settings }
    },
    enabled: scheduleId != null,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  })
  const reload = useCallback(() => { void q.refetch() }, [q])
  const status: QueryStatus = q.isError ? 'error' : q.data ? 'ready' : 'loading'
  return {
    status,
    tasks: q.data?.tasks ?? EMPTY_TASKS,
    rows: q.data?.rows ?? EMPTY_ROWS,
    settings: q.data?.settings ?? EMPTY_SETTINGS,
    reload,
  }
}

/** 서버 응답 행 1건을 발주 데이터 캐시에 반영(전체 재조회 없이 해당 행만 교체, 없으면 추가) */
export function useMaterialOrderCache(scheduleId: number | null | undefined): { replaceRow(row: OrderVendorRow): void } {
  const qc = useQueryClient()
  const replaceRow = useCallback((row: OrderVendorRow) => {
    qc.setQueryData<MaterialOrderBundle>([MATERIAL_ORDERS_KEY, scheduleId], prev => {
      if (!prev) return prev
      const idx = prev.rows.findIndex(r => r.orderVendorId === row.orderVendorId)
      const rows = idx >= 0 ? prev.rows.map((r, i) => (i === idx ? row : r)) : [...prev.rows, row]
      return { ...prev, rows }
    })
  }, [qc, scheduleId])
  return { replaceRow }
}

/** 사업장 단위 거래처 목록(연락처 조회용) — 1회 캐시 */
/**
 * 거래처 목록(Q4, GET /schedule/vendors) 쿼리 옵션 — 이 목록을 쓰는 훅은 모두 이 옵션을 써서 같은 키를 공유한다
 * (6단계 간트의 거래처 전화 조회 useShipVendorPhones 포함 — 같은 경로를 두 번 호출하지 않음).
 */
export function vendorDirectoryQueryOptions(compId: number | null | undefined) {
  return {
    queryKey: [VENDOR_DIRECTORY_KEY, compId],
    queryFn: ({ signal }: { signal?: AbortSignal }) => orderApi.getVendorDirectory(signal),
    staleTime: 5 * 60_000,
    retry: false,
  }
}

export function useVendorDirectory(): { byId: Map<number, VendorInfoLite>; status: QueryStatus } {
  const compId = useAuthStore(s => s.user?.compId)
  const q = useQuery<VendorInfoLite[]>(vendorDirectoryQueryOptions(compId))
  const byId = useMemo(() => new Map((q.data ?? []).map(v => [v.vendorId, v] as const)), [q.data])
  return { byId, status: q.isError ? 'error' : q.data ? 'ready' : 'loading' }
}

/** 공종의 거래처 후보 — enabled 일 때만 조회(행 선택·콤보 열기·근거 모달 열기) */
export function useVendorCandidates(taskId: number | null, enabled: boolean): {
  data?: VendorCandidatesResult
  status: QueryStatus
  reload(): void
} {
  const q = useQuery<VendorCandidatesResult>({
    queryKey: [VENDOR_CANDIDATES_KEY, taskId],
    queryFn: () => orderApi.getVendorCandidates(taskId as number),
    enabled: enabled && taskId != null,
    staleTime: 0,
    retry: false,
  })
  const reload = useCallback(() => { void q.refetch() }, [q])
  return { data: q.data, status: q.isError ? 'error' : q.data ? 'ready' : 'loading', reload }
}

// ── 행동 ──────────────────────────────────────────────────────────────

export type SendOutcome =
  /** send=null 이면 응답은 못 받았지만 목록 재조회로 발송이 확인된 경우 */
  | { result: 'SENT'; send: OrderSendResult | null }
  | { result: 'FAILED'; error: OrderApiError; partialMessage?: string }
  /** 응답 없음 + 재조회로도 확인 못 함 → ORDER_MESSAGE.sendResultUnknown 표시 */
  | { result: 'UNKNOWN'; partialMessage?: string }

export interface BatchItemOutcome {
  orderVendorId: number
  status: 'SUCCESS' | 'FAILED'
  /** FAILED 일 때 서버 건별 코드를 OrderApiError 로 정규화 */
  error?: OrderApiError
}

export interface BatchOutcome {
  succeeded: number
  failed: number
  items: BatchItemOutcome[]
}

export interface SendOptions {
  /** 발송 직전 대기 중인 저장을 끝까지 실행(useOrderDraft.flush). false 를 돌려주면 발송하지 않는다 */
  flush?: () => Promise<boolean>
}

function batchItemError(it: OrderBatchItem): OrderApiError {
  const inner = it.data && typeof it.data === 'object' ? (it.data as { reason?: unknown; field?: unknown }) : null
  return new OrderApiError(it.code ?? 'UNKNOWN', null, {
    ...(inner && typeof inner.reason === 'string' ? { reason: inner.reason } : {}),
    ...(inner && typeof inner.field === 'string' ? { field: inner.field } : {}),
    ...(it.data != null ? { data: it.data } : {}),
  })
}

/**
 * 쓰기 동작 — 성공 토스트·캐시·신호 무효화는 이 훅이, 오류 표시는 호출 화면이 맡는다.
 * 쓰기 성공 뒤 ['material-orders', scheduleId] 와 신호·할일을 재조회하고, 후보 캐시는 해당 공종만 무효화한다.
 */
export function useOrderActions(ctx: { scheduleId: number; siteId: number }) {
  const { scheduleId } = ctx
  const qc = useQueryClient()
  const invalidateSignals = useInvalidateSignals()
  const cache = useMaterialOrderCache(scheduleId)

  const refresh = useCallback((taskId?: number) => {
    void qc.invalidateQueries({ queryKey: [MATERIAL_ORDERS_KEY, scheduleId] })
    if (taskId != null) void qc.invalidateQueries({ queryKey: [VENDOR_CANDIDATES_KEY, taskId] })
    else void qc.invalidateQueries({ queryKey: [VENDOR_CANDIDATES_KEY] })
    void invalidateSignals()
  }, [qc, scheduleId, invalidateSignals])

  /** A3 거래처 지정 */
  const assign = useCallback(async (taskId: number, vendorId: number): Promise<OrderVendorRow> => {
    const row = await orderApi.assignVendor(taskId, vendorId)
    toast.success(ORDER_MESSAGE.assignedToast(row.vendorName))
    refresh(taskId)
    return row
  }, [refresh])

  /** A6 거래처 변경 */
  const replace = useCallback(async (orderVendorId: number, vendorId: number) => {
    const res = await orderApi.replaceVendor(orderVendorId, vendorId)
    toast.success(ORDER_MESSAGE.replacedToast(res.created.vendorName))
    refresh(res.created.taskId)
    return res
  }, [refresh])

  /** A5 지정 해제 */
  const release = useCallback(async (orderVendorId: number, taskId?: number): Promise<void> => {
    await orderApi.releaseVendor(orderVendorId)
    toast.success(ORDER_MESSAGE.releasedToast)
    refresh(taskId)
  }, [refresh])

  /** C9 발주일·입고희망일 변경(즉시 적용, 경고·영향은 응답에서 호출 화면이 표시) */
  const changeDates = useCallback(async (
    taskId: number, patch: { orderDate?: string | null; deliveryDate?: string | null },
  ): Promise<OrderScheduleResult> => {
    const res = await orderApi.patchOrderSchedule(taskId, patch)
    refresh(taskId)
    return res
  }, [refresh])

  /**
   * A7 한 곳 발송(§5.5): 저장 대기분 flush → 발송 → 성공 토스트·재조회.
   * 응답 없음(네트워크·타임아웃)이면 재전송하지 않고 A1 을 한 번 조회해 lastSend.orderSendId 가 달라졌는지로 판정한다.
   */
  const send = useCallback(async (row: OrderVendorRow, opts: SendOptions = {}): Promise<SendOutcome> => {
    if (opts.flush) {
      let flushed = false
      try { flushed = await opts.flush() } catch { flushed = false }
      if (!flushed) return { result: 'FAILED', error: new OrderApiError('SAVE_FAILED', null) }
    }
    const beforeSendId = row.lastSend?.orderSendId ?? null
    try {
      const res = await orderApi.sendOrder(row.orderVendorId)
      toast.success(ORDER_MESSAGE.sentToast(row.vendorName))
      refresh(row.taskId)
      return { result: 'SENT', send: res }
    } catch (e) {
      const err = toOrderApiError(e)
      if (err.code === ORDER_ERROR_NETWORK) {
        let confirmed = false
        try {
          const list = await orderApi.getOrderVendors(row.scheduleId)
          const found = list.find(r => r.orderVendorId === row.orderVendorId)
          const afterId = found?.lastSend?.orderSendId ?? null
          confirmed = afterId != null && afterId !== beforeSendId
        } catch {
          confirmed = false
        }
        refresh(row.taskId)
        if (confirmed) {
          toast.success(ORDER_MESSAGE.sentToast(row.vendorName))
          return { result: 'SENT', send: null }
        }
        return { result: 'UNKNOWN' }
      }
      refresh(row.taskId)   // 상태 불변이지만 목록을 새로 조회해 맞춘다
      return { result: 'FAILED', error: err }
    }
  }, [refresh])

  /**
   * 결과만 직접등록 [발주 기록](SIF-3) — 저장 대기분 flush → A10 대행 입력(가능 회신) → 성공 토스트·재조회. 알림톡은 나가지 않는다.
   * 응답 없음이면 다시 보내지 않고 목록을 새로 불러와 결과 미확인으로 돌려준다.
   */
  const record = useCallback(async (row: OrderVendorRow, opts: SendOptions = {}): Promise<SendOutcome> => {
    if (opts.flush) {
      let flushed = false
      try { flushed = await opts.flush() } catch { flushed = false }
      if (!flushed) return { result: 'FAILED', error: new OrderApiError('SAVE_FAILED', null) }
    }
    try {
      await orderApi.recordOrder(row.orderVendorId)
      toast.success(ORDER_MESSAGE.recordedToast(row.vendorName))
      refresh(row.taskId)
      return { result: 'SENT', send: null }
    } catch (e) {
      const err = toOrderApiError(e)
      refresh(row.taskId)
      return err.code === ORDER_ERROR_NETWORK ? { result: 'UNKNOWN' } : { result: 'FAILED', error: err }
    }
  }, [refresh])

  /** A8 일괄 발송 — 건별 결과. 요청 자체가 실패하면 목록을 새로 조회한 뒤 OrderApiError 를 던진다 */
  const sendBatch = useCallback(async (rows: OrderVendorRow[], opts: SendOptions = {}): Promise<BatchOutcome> => {
    if (opts.flush) {
      let flushed = false
      try { flushed = await opts.flush() } catch { flushed = false }
      if (!flushed) throw new OrderApiError('SAVE_FAILED', null)
    }
    try {
      const res = await orderApi.sendOrderBatch(rows.map(r => r.orderVendorId))
      const items: BatchItemOutcome[] = res.items.map(it => ({
        orderVendorId: Number(it.ref),
        status: it.status,
        ...(it.status === 'FAILED' ? { error: batchItemError(it) } : {}),
      }))
      const succeeded = items.filter(i => i.status === 'SUCCESS').length
      const failed = items.length - succeeded
      toast[failed > 0 ? 'warning' : 'success'](ORDER_MESSAGE.batchSentToast(succeeded, failed))
      refresh()
      return { succeeded, failed, items }
    } catch (e) {
      refresh()
      throw toOrderApiError(e)
    }
  }, [refresh])

  /** 불가 회신의 가능한 날짜로 입고희망일을 바꾼 뒤(C9) 다시 보냄(A7). C9 만 성공하고 A7 이 실패하면 부분 성공 문구를 함께 돌려줌 */
  const resendWithAvailDate = useCallback(async (row: OrderVendorRow, opts: SendOptions = {}): Promise<SendOutcome> => {
    const avail = row.lastReply?.availDate
    const md = formatMD(avail)
    if (!avail || !md) throw new OrderApiError('NO_AVAIL_DATE', null)
    await changeDates(row.taskId, { deliveryDate: avail })
    const out = await send(row, opts)
    if (out.result === 'SENT') return out
    return { ...out, partialMessage: ORDER_MESSAGE.partialDateOnly(md) }
  }, [changeDates, send])

  /** 서버 응답 행을 캐시에 반영(자동저장 성공·충돌 해소) */
  const applyRow = cache.replaceRow

  return { assign, replace, release, changeDates, send, record, sendBatch, resendWithAvailDate, applyRow }
}

// ── 입력 초안 자동저장 ────────────────────────────────────────────────

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function emptyDraft(): OrderDraft {
  return { items: [emptyDraftItem()], liftReqYn: '', deliveryAddress: '', orderNote: '' }
}

interface DraftOpts {
  /** 저장 성공 응답(해당 행만 캐시 교체) */
  onSaved(row: OrderVendorRow): void
  /** 409 ORDER_CONTENT_CONFLICT — current 는 서버의 현재 행 */
  onConflict(current: OrderVendorRow): void
}

/**
 * 선택 행의 발주정보 입력 초안. 변경 후 600ms 뒤 A4 저장(단일 비행, 저장 중 변경은 마지막 값 1회로 합침).
 * flush() 는 대기 저장을 즉시 실행하고 끝날 때까지 기다린다(행 전환·팝업 닫기·발송 직전에 호출) — 저장 실패면 false.
 * 검증 오류가 있어도 오류 없는 필드는 저장한다(items 는 전체 목록이 유효할 때만). 검증 오류만 남은 경우 flush 는 true.
 * 언마운트 시 대기 중인 저장은 버리지 않고 마지막으로 실행한다.
 */
export function useOrderDraft(row: OrderVendorRow | null, opts: DraftOpts): {
  draft: OrderDraft
  setDraft(patch: Partial<OrderDraft>): void
  errors: DraftErrors
  saveState: SaveState
  /** saveState 가 error 일 때 표시할 문구 */
  errorMessage: string | null
  flush(): Promise<boolean>
  discard(): void
  overwrite(current: OrderVendorRow): Promise<void>
} {
  const optsRef = useRef(opts)
  optsRef.current = opts

  const rowId = row?.orderVendorId ?? null
  const [trackedId, setTrackedId] = useState<number | null>(rowId)
  const [draft, setDraftState] = useState<OrderDraft>(() => (row ? draftFromRow(row) : emptyDraft()))
  const [serverErr, setServerErr] = useState<DraftErrors | null>(null)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // 다른 행으로 바뀌면 렌더 중에 초안을 바로 교체(이전 행 값이 한 프레임이라도 보이지 않게)
  if (trackedId !== rowId) {
    setTrackedId(rowId)
    setDraftState(row ? draftFromRow(row) : emptyDraft())
    setServerErr(null)
    setSaveState('idle')
    setErrorMessage(null)
  }

  const draftRef = useRef(draft)
  const syncedRef = useRef<OrderVendorRow | null>(null)   // 가장 최근에 확인된 서버 행(expectedVer 의 근거)
  const conflictRef = useRef<OrderVendorRow | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const drainRef = useRef<Promise<boolean> | null>(null)
  const requestedRef = useRef(false)
  const mountedRef = useRef(true)

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
  }

  const safe = <T,>(fn: (v: T) => void) => (v: T) => { if (mountedRef.current) fn(v) }
  const setSaveStateSafe = safe(setSaveState)
  const setErrorMessageSafe = safe(setErrorMessage)
  const setServerErrSafe = safe(setServerErr)

  const saveOnce = async (): Promise<boolean> => {
    const synced = syncedRef.current
    if (!synced) return true
    const patch = toOrderPatch(synced, draftRef.current)
    if (!patch) return true
    if (savedTimerRef.current) { clearTimeout(savedTimerRef.current); savedTimerRef.current = null }
    setSaveStateSafe('saving')
    setErrorMessageSafe(null)
    try {
      const saved = await orderApi.patchOrder(synced.orderVendorId, patch)
      syncedRef.current = saved
      optsRef.current.onSaved(saved)
      setSaveStateSafe('saved')
      savedTimerRef.current = setTimeout(() => {
        savedTimerRef.current = null
        if (mountedRef.current) setSaveState(s => (s === 'saved' ? 'idle' : s))
      }, DRAFT_SAVED_VISIBLE_MS)
      return true
    } catch (e) {
      const err = toOrderApiError(e)
      if (err.code === 'ORDER_CONTENT_CONFLICT' && err.data && typeof err.data === 'object') {
        const current = err.data as OrderVendorRow
        conflictRef.current = current
        setSaveStateSafe('error')
        setErrorMessageSafe(orderErrorMessage(err))
        optsRef.current.onConflict(current)
        return false
      }
      if (err.code === 'VALIDATION_ERROR') {
        const target = mapServerField(err.field)
        if (target) {
          const overlay: DraftErrors = { items: {} }
          const msg = ORDER_MESSAGE.panelCheckInput
          if (target.kind === 'item') overlay.items[target.index] = { [target.key]: msg }
          else if (target.name === 'deliveryAddress') overlay.deliveryAddress = msg
          else if (target.name === 'orderNote') overlay.orderNote = msg
          else if (target.name === 'liftReqYn') overlay.liftReqYn = msg
          setServerErrSafe(overlay)
        }
      }
      setSaveStateSafe('error')
      setErrorMessageSafe(orderErrorMessage(err))
      return false
    }
  }

  const drain = (): Promise<boolean> => {
    if (drainRef.current) {
      requestedRef.current = true   // 저장 중 들어온 변경은 마지막 값 1회로 합쳐 이어서 저장
      return drainRef.current
    }
    const p = (async () => {
      let ok = true
      do {
        requestedRef.current = false
        ok = await saveOnce()
        if (!ok) break
      } while (requestedRef.current)
      return ok
    })().finally(() => { drainRef.current = null })
    drainRef.current = p
    return p
  }

  const setDraft = useCallback((patch: Partial<OrderDraft>) => {
    if (!syncedRef.current) return
    const next = { ...draftRef.current, ...patch }
    draftRef.current = next
    setDraftState(next)
    setServerErr(null)
    clearTimer()
    timerRef.current = setTimeout(() => { timerRef.current = null; void drain() }, DRAFT_SAVE_DEBOUNCE_MS)
    // drain·clearTimer 는 ref 만 사용하므로 안정
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 서버 행과 초안 동기화 — 행이 바뀌었거나 다른 곳에서 내용이 바뀐 경우(내 저장 응답은 syncedRef 가 이미 같은 ver)
  useEffect(() => {
    if (!row) { syncedRef.current = null; return }
    const prev = syncedRef.current
    if (!prev || prev.orderVendorId !== row.orderVendorId) {
      clearTimer()
      syncedRef.current = row
      conflictRef.current = null
      draftRef.current = draftFromRow(row)
      return
    }
    if (row.contentVer === prev.contentVer) return
    const dirty = timerRef.current != null || drainRef.current != null || toOrderPatch(prev, draftRef.current) != null
    if (dirty) return   // 입력 중이면 이전 ver 를 유지해 저장 때 서버가 충돌을 알려주게 한다
    syncedRef.current = row
    const next = draftFromRow(row)
    draftRef.current = next
    setDraftState(next)
  }, [row])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; void drain() }   // 대기 중이던 저장은 버리지 않음
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const flush = useCallback(async (): Promise<boolean> => {
    clearTimer()
    return drain()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const discard = useCallback(() => {
    clearTimer()
    const base = conflictRef.current ?? syncedRef.current
    if (conflictRef.current) {
      syncedRef.current = conflictRef.current
      optsRef.current.onSaved(conflictRef.current)
      conflictRef.current = null
    }
    const next = base ? draftFromRow(base) : emptyDraft()
    draftRef.current = next
    setDraftState(next)
    setServerErr(null)
    setSaveState('idle')
    setErrorMessage(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const overwrite = useCallback(async (current: OrderVendorRow): Promise<void> => {
    clearTimer()
    syncedRef.current = current   // 서버 현재 contentVer 로 expectedVer 를 맞춰 내 입력을 다시 전송
    conflictRef.current = null
    await drain()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const errors = useMemo<DraftErrors>(() => {
    const base = validateDraft(draft)
    if (!serverErr) return base
    return {
      items: { ...serverErr.items, ...base.items },
      itemsTotal: base.itemsTotal ?? serverErr.itemsTotal,
      liftReqYn: base.liftReqYn ?? serverErr.liftReqYn,
      deliveryAddress: base.deliveryAddress ?? serverErr.deliveryAddress,
      orderNote: base.orderNote ?? serverErr.orderNote,
    }
  }, [draft, serverErr])

  return { draft, setDraft, errors, saveState, errorMessage, flush, discard, overwrite }
}

/** 초안 오류 여부(검증 오류가 있으면 발송 전 점검이 막는다) */
export function draftHasErrors(e: DraftErrors): boolean {
  return hasDraftErrors(e)
}
